import React from 'react';
import { Group, Rect, Text } from 'react-konva';
import type { ComputedLabel, LabelPosition } from '../../../core/assistant/models/ViewRules';

const LABEL_FONT_SIZE = 11;
const LABEL_LINE_HEIGHT = 14;
const LABEL_PADDING_X = 6;
const LABEL_PADDING_Y = 3;
const LABEL_GAP = 4;
const LABEL_CORNER_RADIUS = 4;
const LABEL_MIN_WIDTH = 56;
const LABEL_MAX_LINES = 2;

const getLabelYPosition = (
  position: LabelPosition,
  nodeHeight: number,
  labelHeight: number,
  labelIndex: number
): number => {
  const stackOffset = labelIndex * (labelHeight + LABEL_GAP);
  switch (position) {
    case 'above':
      return -labelHeight - LABEL_GAP - stackOffset;
    case 'below':
      return nodeHeight + LABEL_GAP + stackOffset;
    case 'inline':
      return nodeHeight - labelHeight - LABEL_PADDING_Y - stackOffset;
    default:
      return nodeHeight + LABEL_GAP + stackOffset;
  }
};

interface DisplayRuleLabelProps {
  label: ComputedLabel;
  nodeWidth: number;
  nodeHeight: number;
  positionOffset: number;
}

export const DisplayRuleLabel: React.FC<DisplayRuleLabelProps> = ({
  label,
  nodeWidth,
  nodeHeight,
  positionOffset,
}) => {
  const textColor = label.style?.textColor || '#FFFFFF';
  const backgroundColor = label.style?.backgroundColor || '#6B7280';

  const estimatedTextWidth = label.text.length * (LABEL_FONT_SIZE * 0.6);
  const maxLabelWidth = Math.max(LABEL_MIN_WIDTH, nodeWidth - 8);
  const labelWidth = Math.min(
    Math.max(LABEL_MIN_WIDTH, estimatedTextWidth + LABEL_PADDING_X * 2),
    maxLabelWidth,
  );
  const textWidth = Math.max(1, labelWidth - LABEL_PADDING_X * 2);
  const lineCount = Math.min(
    LABEL_MAX_LINES,
    Math.max(1, Math.ceil(estimatedTextWidth / textWidth)),
  );
  const labelHeight = LABEL_LINE_HEIGHT * lineCount + LABEL_PADDING_Y * 2;

  const y = getLabelYPosition(label.position, nodeHeight, labelHeight, positionOffset);
  const x = (nodeWidth - labelWidth) / 2;

  return (
    <Group x={x} y={y} listening={false}>
      <Rect
        width={labelWidth}
        height={labelHeight}
        fill={backgroundColor}
        cornerRadius={LABEL_CORNER_RADIUS}
        shadowColor="rgba(0,0,0,0.15)"
        shadowBlur={2}
        shadowOffsetY={1}
      />
      <Text
        x={LABEL_PADDING_X}
        y={LABEL_PADDING_Y}
        width={textWidth}
        height={LABEL_LINE_HEIGHT * lineCount}
        text={label.text}
        fontSize={LABEL_FONT_SIZE}
        lineHeight={LABEL_LINE_HEIGHT / LABEL_FONT_SIZE}
        fill={textColor}
        align="center"
        wrap="word"
        ellipsis
      />
    </Group>
  );
};

interface DisplayRuleLabelsProps {
  labels: ComputedLabel[];
  nodeWidth: number;
  nodeHeight: number;
}

export const DisplayRuleLabels: React.FC<DisplayRuleLabelsProps> = ({
  labels,
  nodeWidth,
  nodeHeight,
}) => {
  if (labels.length === 0) return null;

  const aboveLabels = labels.filter((l) => l.position === 'above');
  const belowLabels = labels.filter((l) => l.position === 'below');
  const inlineLabels = labels.filter((l) => l.position === 'inline');

  return (
    <>
      {aboveLabels.map((label, index) => (
        <DisplayRuleLabel
          key={`above-${index}`}
          label={label}
          nodeWidth={nodeWidth}
          nodeHeight={nodeHeight}
          positionOffset={index}
        />
      ))}
      {belowLabels.map((label, index) => (
        <DisplayRuleLabel
          key={`below-${index}`}
          label={label}
          nodeWidth={nodeWidth}
          nodeHeight={nodeHeight}
          positionOffset={index}
        />
      ))}
      {inlineLabels.map((label, index) => (
        <DisplayRuleLabel
          key={`inline-${index}`}
          label={label}
          nodeWidth={nodeWidth}
          nodeHeight={nodeHeight}
          positionOffset={index}
        />
      ))}
    </>
  );
};
